import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import {Typography, Box, FormControl, Select, MenuItem} from '@mui/material'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {useMaterialVariationsForm} from '../../../hooks/useMaterialVariationsForm'

/**
 * MaterialVariationsStep Component
 *
 * Wizard step for configuring material variations (slump and set control).
 * This component is displayed when "Continue with material variations" is clicked
 * in the Material step.
 */
export const MaterialVariationsStep: React.FC = () => {
  const {t} = useTranslation()
  const {
    slump,
    setControl,
    handleSlumpChange,
    handleSetControlChange,
    slumpOptions,
    setControlOptions,
    isSlumpAvailable,
    isSetControlAvailable,
    selectedMaterialDescription
  } = useMaterialVariationsForm()

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '24px'
      }}
    >
      {/* Read-only Mix type field */}
      <Box sx={{width: '100%'}}>
        <Box
          sx={{
            backgroundColor: '#ffffff',
            // border: '1px solid #d1d9e0',
            borderRadius: '4px',
            height: '48px',
            display: 'flex',
            alignItems: 'center',
            padding: '4px'
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              paddingX: '8px',
              overflow: 'hidden'
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: '12px',
                lineHeight: '16px',
                color: '#52667a'
              }}
            >
              {t('orderIntake.materialVariations.mixType')}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 500,
                fontSize: '16px',
                lineHeight: '24px',
                color: '#29333d',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {selectedMaterialDescription}
            </Typography>
          </Box>
          <Box sx={{display: 'flex', alignItems: 'center', paddingRight: '8px'}}>
            <InfoOutlinedIcon sx={{width: '16px', height: '16px', color: '#52667a'}} />
          </Box>
        </Box>
      </Box>

      {/* Slump Field */}
      {isSlumpAvailable && (
        <Box sx={{width: '100%', display: 'flex', flexDirection: 'column', gap: '12px'}}>
          <FormControl fullWidth>
            <Box
              sx={{
                backgroundColor: '#ffffff',
                border: '1px solid #d1d9e0',
                borderRadius: '4px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                padding: '4px',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: '#b3bec9'
                }
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  paddingX: '8px',
                  overflow: 'hidden'
                }}
              >
                <Select
                  value={slump}
                  onChange={handleSlumpChange}
                  displayEmpty
                  renderValue={(value) => {
                    if (!value) {
                      return (
                        <Typography
                          sx={{
                            fontFamily: 'Inter',
                            fontWeight: 500,
                            fontSize: '16px',
                            lineHeight: '24px',
                            color: '#52667a'
                          }}
                        >
                          {t('orderIntake.materialVariations.slump')}
                        </Typography>
                      )
                    }
                    const option = slumpOptions.find((opt) => opt.value === value)
                    return (
                      <Typography
                        sx={{
                          fontFamily: 'Inter',
                          fontWeight: 500,
                          fontSize: '16px',
                          lineHeight: '24px',
                          color: '#29333d'
                        }}
                      >
                        {option?.label || value}
                      </Typography>
                    )
                  }}
                  sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none'
                    },
                    '& .MuiSelect-select': {
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center'
                    }
                  }}
                  IconComponent={KeyboardArrowRightIcon}
                  data-test-id="mix-characteristics-slump-select"
                >
                  {slumpOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Box>
          </FormControl>
          <Typography
            sx={{
              fontFamily: 'Inter',
              fontWeight: 400,
              fontSize: '16px',
              lineHeight: '22px',
              color: '#52667a'
            }}
          >
            {t('orderIntake.materialVariations.slumpDescription')}
          </Typography>
        </Box>
      )}

      {/* Set Control Field */}
      {isSetControlAvailable && (
        <Box sx={{width: '100%', display: 'flex', flexDirection: 'column', gap: '12px'}}>
          <FormControl fullWidth>
            <Box
              sx={{
                backgroundColor: '#ffffff',
                border: '1px solid #d1d9e0',
                borderRadius: '4px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                padding: '4px',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: '#b3bec9'
                }
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  paddingX: '8px',
                  overflow: 'hidden'
                }}
              >
                <Select
                  value={setControl}
                  onChange={handleSetControlChange}
                  displayEmpty
                  renderValue={(value) => {
                    if (!value) {
                      return (
                        <Typography
                          sx={{
                            fontFamily: 'Inter',
                            fontWeight: 500,
                            fontSize: '16px',
                            lineHeight: '24px',
                            color: '#52667a'
                          }}
                        >
                          {t('orderIntake.materialVariations.setControl')}
                        </Typography>
                      )
                    }
                    const option = setControlOptions.find((opt) => opt.value === value)
                    return (
                      <Typography
                        sx={{
                          fontFamily: 'Inter',
                          fontWeight: 500,
                          fontSize: '16px',
                          lineHeight: '24px',
                          color: '#29333d'
                        }}
                      >
                        {option?.label || value}
                      </Typography>
                    )
                  }}
                  sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none'
                    },
                    '& .MuiSelect-select': {
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center'
                    }
                  }}
                  IconComponent={KeyboardArrowRightIcon}
                  data-test-id="mix-characteristics-set-control-select"
                >
                  {setControlOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            </Box>
          </FormControl>
          <Typography
            sx={{
              fontFamily: 'Inter',
              fontWeight: 400,
              fontSize: '16px',
              lineHeight: '22px',
              color: '#52667a'
            }}
          >
            {t('orderIntake.materialVariations.setControlDescription')}
          </Typography>
        </Box>
      )}
    </Box>
  )
}
